/**
 * @fileOverView
 * @description In-page replacements for window.alert, confirm and prompt<br>
 * Embedded browsers (the Claude desktop browser pane, Slack and Instagram webviews)
 * suppress the native dialogs: confirm() returns false at once and alert() is never
 * shown. These render Bootstrap modal markup instead and return promises.
 * Bootstrap's JS is not loaded, so showing, hiding and focus are handled here.
 * @copyright 2015-present Flatirontek LLC
 **/

'use strict';

let modal = null;       //the one reusable modal element, built on first use
let backdrop = null;
let active = null;      //{finish, previousFocus} for the dialog on screen
const queue = [];       //native dialogs block, so calls made while one is open wait their turn
let idCounter = 0;

function build(){
    const id = 'vybitDialog' + (++idCounter);
    modal = document.createElement('div');
    modal.className = 'modal fade';
    modal.tabIndex = -1;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', id + 'Title');
    modal.setAttribute('aria-describedby', id + 'Message');
    modal.innerHTML =
        '<div class="modal-dialog modal-dialog-centered">' +
            '<form class="modal-content" novalidate>' +
                '<div class="modal-header">' +
                    '<h5 class="modal-title" id="' + id + 'Title"></h5>' +
                '</div>' +
                '<div class="modal-body">' +
                    '<label class="mb-0" id="' + id + 'Message" for="' + id + 'Input" style="white-space:pre-line;overflow-wrap:anywhere"></label>' +
                    '<input class="form-control mt-3" id="' + id + 'Input" autocomplete="off">' +
                '</div>' +
                '<div class="modal-footer">' +
                    '<button type="button" class="btn btn-outline-secondary" data-dialog="cancel"></button>' +
                    '<button type="submit" class="btn" data-dialog="ok"></button>' +
                '</div>' +
            '</form>' +
        '</div>';
    backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop fade';

    const form = modal.querySelector('form');
    form.addEventListener('submit', function(event){
        event.preventDefault();
        if(active) active.finish(true);
    });
    modal.querySelector('[data-dialog="cancel"]').addEventListener('click', function(){
        if(active) active.finish(false);
    });
    //a click on the dimmed area outside the dialog cancels, like Esc
    modal.addEventListener('mousedown', function(event){
        if(event.target === modal && active) active.finish(false);
    });
    modal.addEventListener('keydown', function(event){
        if(!active) return;
        if(event.key === 'Escape'){
            event.preventDefault();
            active.finish(false);
        }
        else if(event.key === 'Tab'){
            //keep focus inside the dialog
            const focusable = Array.prototype.filter.call(
                modal.querySelectorAll('input, button'),
                function(el){ return el.offsetParent !== null; }
            );
            if(!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if(event.shiftKey && (document.activeElement === first || document.activeElement === modal)){
                event.preventDefault();
                last.focus();
            }
            else if(!event.shiftKey && document.activeElement === last){
                event.preventDefault();
                first.focus();
            }
        }
    });
}

function show(spec){
    if(!modal) build();
    const title = modal.querySelector('.modal-title');
    const message = modal.querySelector('label');
    const input = modal.querySelector('input');
    const cancel = modal.querySelector('[data-dialog="cancel"]');
    const ok = modal.querySelector('[data-dialog="ok"]');

    title.textContent = spec.title;
    message.textContent = spec.message;
    input.style.display = spec.kind === 'prompt' ? '' : 'none';
    input.type = spec.inputType || 'text';
    input.placeholder = spec.placeholder || '';
    input.value = spec.defaultValue || '';
    cancel.style.display = spec.kind === 'alert' ? 'none' : '';
    cancel.textContent = spec.cancelText;
    ok.textContent = spec.okText;
    ok.className = 'btn ' + (spec.danger ? 'btn-danger' : 'btn-primary');

    active = {
        previousFocus: document.activeElement,
        finish: function(confirmed){
            const done = active;
            active = null;
            modal.classList.remove('show');
            backdrop.classList.remove('show');
            modal.style.display = 'none';
            if(modal.parentNode) modal.parentNode.removeChild(modal);
            if(backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
            document.body.classList.remove('modal-open');
            if(done.previousFocus && done.previousFocus.focus) done.previousFocus.focus();
            spec.resolve(confirmed, input.value);
            next();
        }
    };

    document.body.appendChild(backdrop);
    document.body.appendChild(modal);
    document.body.classList.add('modal-open');
    modal.style.display = 'block';
    //force a reflow so the fade transition runs
    void modal.offsetWidth;
    modal.classList.add('show');
    backdrop.classList.add('show');
    (spec.kind === 'prompt' ? input : ok).focus();
}

function next(){
    if(active || !queue.length) return;
    show(queue.shift());
}

function open(spec){
    //no DOM (unit tests): resolve as a cancel rather than throw
    if(typeof document === 'undefined'){
        return Promise.resolve(spec.kind === 'confirm' ? false : spec.kind === 'prompt' ? null : undefined);
    }
    return new Promise(function(resolve){
        spec.resolve = function(confirmed, value){
            if(spec.kind === 'confirm') resolve(confirmed);
            else if(spec.kind === 'prompt') resolve(confirmed ? value : null);
            else resolve();
        };
        queue.push(spec);
        //an email sign-in link can ask for the address while the page is still loading
        if(document.body) next();
        else document.addEventListener('DOMContentLoaded', next);
    });
}

function text(value){
    return value === undefined || value === null ? '' : String(value);
}

module.exports = {

    /**
     * In-page alert.
     * @param {string} message
     * @param {{title?: string, okText?: string}} [options]
     * @returns {Promise<void>} resolves when dismissed
     */
    alert: function(message, options){
        options = options || {};
        return open({
            kind: 'alert',
            message: text(message),
            title: options.title || 'Vybit',
            okText: options.okText || 'OK'
        });
    },

    /**
     * In-page confirm.
     * @param {string} message
     * @param {{title?: string, okText?: string, cancelText?: string, danger?: boolean}} [options]
     *   danger styles the OK button red; pass the action as okText ("Delete vybit"), not "OK"
     * @returns {Promise<boolean>} true on OK, false on Cancel, Esc or a click outside
     */
    confirm: function(message, options){
        options = options || {};
        return open({
            kind: 'confirm',
            message: text(message),
            title: options.title || 'Please confirm',
            okText: options.okText || 'OK',
            cancelText: options.cancelText || 'Cancel',
            danger: !!options.danger
        });
    },

    /**
     * In-page prompt.
     * @param {string} message
     * @param {{title?: string, okText?: string, cancelText?: string, placeholder?: string, type?: string, defaultValue?: string}} [options]
     *   type is the input type, e.g. 'email'
     * @returns {Promise<string|null>} the entered text, or null on Cancel, Esc or a click outside
     */
    prompt: function(message, options){
        options = options || {};
        return open({
            kind: 'prompt',
            message: text(message),
            title: options.title || 'Vybit',
            okText: options.okText || 'OK',
            cancelText: options.cancelText || 'Cancel',
            placeholder: options.placeholder,
            inputType: options.type,
            defaultValue: options.defaultValue
        });
    }
};
